import cookie from "js-cookie";
import { getClient } from "lib/apolloClient";
import { Logger } from "lib/logger";
import type { Session } from "@shared/graphql.types";
import { track } from "tracking/client/segment";
import { getBPFingerprint } from "lib/fingerprint";
import { SESSION_QUERY } from "./session.gql";

// =================================

export const LAST_EVENT_TIMESTAMP_STORAGE_KEY = "lastEventDate";

export const updateLastEventTimestamp = (): void => {
  localStorage.setItem(
    LAST_EVENT_TIMESTAMP_STORAGE_KEY,
    new Date().toISOString()
  );
};

const getLastEventTimestamp = (): string | null => {
  return localStorage.getItem(LAST_EVENT_TIMESTAMP_STORAGE_KEY);
};

// =================================

export type SessionCookie = Session & {
  fingerprintId?: string;
  userId?: string;
};

export const STORAGE_KEY = "trackingSession";

const getSessionCookie = (): SessionCookie | undefined => {
  const item = cookie.get(STORAGE_KEY);

  if (item) {
    return JSON.parse(item);
  }
};

const setSessionCookie = (item: SessionCookie): string | undefined => {
  return cookie.set(STORAGE_KEY, JSON.stringify(item));
};

let promise;

export async function querySession(): Promise<Session | undefined> {
  if (promise) return promise;

  const fingerprintCookie = await getBPFingerprint();

  if (!fingerprintCookie?.fingerprintId) return;

  const { fingerprintId } = fingerprintCookie;

  const lastEventTimestamp =
    getLastEventTimestamp() || new Date().toISOString();

  promise = getClient()
    .query({
      query: SESSION_QUERY,
      variables: {
        fingerprintId,
        lastEventTimestamp
      }
    })
    .then(({ data: { session } }: { data: { session: Session } }) => {
      const item: SessionCookie = {
        ...fingerprintCookie,
        ...session
      };

      setSessionCookie(item);

      if (session.isNew) {
        track({
          event: "Session Started",
          properties: {}
        });
      }
      return session;
    });

  return promise;
}

const THIRTY_MINUTES_MS = 1000 * 60 * 30;

export const SESSION_ACTIVITY_WINDOW = THIRTY_MINUTES_MS;

const insideTheActivityWindow = (timestamp: string): boolean => {
  const windowStart = new Date().getTime() - SESSION_ACTIVITY_WINDOW;
  return new Date(timestamp).getTime() >= windowStart;
};

export async function getSession(
  fetchSession = querySession,
  log = Logger(`session`)
): Promise<Session | undefined> {
  try {
    const session: SessionCookie | undefined = getSessionCookie();

    const lastActivityTimestamp = getLastEventTimestamp();

    if (
      session &&
      lastActivityTimestamp &&
      insideTheActivityWindow(lastActivityTimestamp)
    ) {
      return session;
    }

    return await fetchSession();
  } catch (e) {
    log.error(`Failed to load session`, e);
    cookie.remove(STORAGE_KEY);
  }
}
